import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { PaymentService } from '../../../core/api/payment.service';
import { PaymentMethod, PaymentCreate } from '../../../core/models/payment.model';
import { parseApiDateTime, toApiDateTime } from '../../../shared/utils/date.utils';
import { DatetimeInputComponent } from '../../../shared/components/datetime-input/datetime-input.component';

@Component({
  selector: 'app-payment-create',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatCardModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule,
    MatCheckboxModule, MatSnackBarModule, MatProgressSpinnerModule,
    MatDividerModule, RouterLink,
    DatetimeInputComponent,
  ],
  templateUrl: './payment-create.component.html',
  styleUrl: './payment-create.component.scss',
})
export class PaymentCreateComponent implements OnInit {
  form: FormGroup;
  submitting = false;
  rentalId: number | null = null;
  PaymentMethod = PaymentMethod;
  paymentMethodOptions = [
    { value: PaymentMethod.CASH, label: 'Наличные' },
    { value: PaymentMethod.CARD, label: 'Карта' },
    { value: PaymentMethod.TRANSFER, label: 'Перевод' },
    { value: PaymentMethod.OTHER, label: 'Другое' },
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private paymentService: PaymentService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      rental: [null, Validators.required],
      amount: ['', [Validators.required, Validators.min(0.01)]],
      paymentMethod: [PaymentMethod.CASH, Validators.required],
      isPaid: [true],
      paymentDate: [null as Date | null],
      description: [''],
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('rental');
    if (id) {
      this.rentalId = Number(id);
      this.form.patchValue({ rental: this.rentalId });
    }
  }

  onSubmit(): void {
    if (this.form.invalid || !this.rentalId || this.submitting) return;
    this.submitting = true;
    const v = this.form.value;
    const paymentDate = v.paymentDate ? parseApiDateTime(v.paymentDate) : null;
    const payload: PaymentCreate = {
      rental: this.rentalId,
      amount: v.amount,
      payment_method: v.paymentMethod,
      is_paid: v.isPaid,
      payment_date: paymentDate ? toApiDateTime(paymentDate) : null,
      description: v.description || '',
    };
    this.paymentService.create(payload).subscribe({
      next: () => {
        this.snackBar.open('Платёж создан', 'ОК', { duration: 3000 });
        this.router.navigate(['/rentals', this.rentalId]);
      },
      error: (err: any) => {
        this.submitting = false;
        const errorData = err.error;
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          this.setServerErrors(errorData);
          const nonField = errorData.all || errorData.non_field_errors;
          if (nonField) {
            const msg = Array.isArray(nonField) ? nonField[0] : String(nonField);
            this.snackBar.open(msg, 'ОК', { duration: 5000 });
          }
        } else {
          const msg = Array.isArray(errorData) ? errorData[0] : 'Ошибка создания';
          this.snackBar.open(msg, 'ОК', { duration: 5000 });
        }
      },
    });
  }

  private readonly apiFieldToControl: Record<string, string> = {
    rental: 'rental',
    amount: 'amount',
    payment_method: 'paymentMethod',
    is_paid: 'isPaid',
    payment_date: 'paymentDate',
    description: 'description',
  };

  private setServerErrors(errors: Record<string, any>): void {
    for (const [apiField, controlName] of Object.entries(this.apiFieldToControl)) {
      const control = this.form.get(controlName);
      if (control && errors.hasOwnProperty(apiField)) {
        const messages = errors[apiField];
        const msg = Array.isArray(messages) ? messages[0] : String(messages);
        control.setErrors({ ...control.errors, serverError: msg });
      }
    }
  }
}
